import {
  ClipboardEvent,
  MouseEvent,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Editable, ReactEditor, Slate, withReact } from 'slate-react';

import { useTranslation } from '@mint-lib/i18n';
import { useFlags } from '@mint-lib/routing-context';
import {
  countTextLength,
  type CustomEditor,
  getInitialValue,
  htmlToSlateNodes,
  renderEditorLeaf,
  typographyVariants,
} from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';
import { Chat } from '@mint-sdk/api-ai-assistant';
import DOMPurify from 'dompurify';
import { createEditor, Node, Transforms } from 'slate';
import { HistoryEditor, withHistory } from 'slate-history';

import { schedulingOptions } from '../../constants/scheduling.js';
import { useAiHubPermissions } from '../../hooks/useAiHubPermissions.jsx';
import {
  ChipDateRangePicker,
  ChipSelect,
  ChipTimepicker,
} from './chip/index.js';
import { FileSelection } from './FileSelection/FileSelection.jsx';
import { useSelectedFiles } from './FileSelection/useSelectedFiles.jsx';
import { serializeSlateToHtml, withNormalization } from './helpers.jsx';
import {
  MessageModeDropdown,
  type MessageMode,
} from './MessageModeDropdown.jsx';
import { NodeSelection } from './NodeSelection/NodeSelection.jsx';
import { renderElement } from './renderElement.jsx';
import { SendMessageButton } from './SendMessageButton.jsx';
import {
  editorWrapperVariants,
  getEditorState,
  getHoverState,
} from './styling.jsx';
import { useScheduleMessage } from './useScheduleMessage.js';

export type TextAreaAiProps = {
  /** max length */
  maxLength?: number;
  /** If 'true' - disabled. */
  isDisabled?: boolean;
  /** readOnly  */
  isReadOnlyAccess?: boolean;
  /** Description of the text area. */
  inputValue?: string;
  /** Function to send the question */
  handleSendQuestion: (
    message: string,
    metadata?: Chat.UserMetadataSchemaType,
  ) => void;
  /** Function to start voice chat */
  onStartVoiceChat?: () => void;
  /** Custom styles */
  classNames?: string;
  /** Placeholder */
  placeholder?: string;
  /** Is available file selection feature */
  withFileSelection?: boolean;
  /** Is available schedule chat feature*/
  withScheduleChat?: boolean;
  /** If TextArea is used in chat, we need to extract specific data depending on the chat. */
  chatId?: string;
  /** If 'true' - interrupt feature available. */
  isInterruptible?: boolean;
  /** If 'true' - interrupt button disabled. */
  isInterruptPending?: boolean;
  /** If 'true' - interrupt action hidden. */
  isInterruptSuccess?: boolean;
  /** Function to interrupt the last request */
  onInterrupt?: () => void;
};

const MAX_CURRENT_TEXT_LENGTH = 9999;

const TextAreaAi = ({
  inputValue,
  placeholder,
  handleSendQuestion,
  onStartVoiceChat,
  classNames,
  maxLength = 2000,
  isReadOnlyAccess = false,
  isDisabled = false,
  isInterruptible = false,
  isInterruptPending,
  isInterruptSuccess,
  onInterrupt,
  withFileSelection,
  withScheduleChat,
  chatId,
}: TextAreaAiProps) => {
  const { t } = useTranslation('@mint-lib/ai-chat');
  const ff = useFlags('@mint/arm', ['textAreaFileSelection', 'aiFileSystem']);

  const { canScheduleChat } = useAiHubPermissions();

  const [value, setValue] = useState<Node[]>(getInitialValue(inputValue));
  const [message, setMessage] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [messageMode, setMessageMode] = useState<MessageMode>('chat');

  const {
    selectedFiles,
    setSelectedFiles,
    selectedFolders,
    setSelectedFolders,
  } = useSelectedFiles(chatId);
  const {
    scheduling,
    setScheduling,
    scheduleTime,
    setScheduleTime,
    scheduleDateRange,
    setScheduleDateRange,
    isScheduleReady,
    isSchedulePending,
    onSchedule,
  } = useScheduleMessage();

  const editor = useMemo(
    () =>
      withNormalization(withReact(withHistory(createEditor() as CustomEditor))),
    [],
  );
  const editorWrapperRef = useRef<HTMLDivElement>(null);

  const preparedValue =
    typeof value === 'string'
      ? [
          {
            type: 'placeholder',
            children: [
              {
                text: value,
              },
            ],
          },
        ]
      : value;

  const showFileSelection =
    withFileSelection && ff.textAreaFileSelection.enabled;

  const length = useMemo(() => {
    return countTextLength(value);
  }, [value]);

  const [isMultiLine, setIsMultiLine] = useState(false);

  useLayoutEffect(() => {
    const hasMultipleNodes = value.length > 1;

    const textbox = editorWrapperRef.current?.querySelector('[role="textbox"]');
    if (!textbox) {
      setIsMultiLine(hasMultipleNodes);
      return;
    }

    const lineHeight =
      Number.parseInt(getComputedStyle(textbox).lineHeight) || 20;
    const visuallyMultiLine = textbox.scrollHeight > lineHeight * 1.5;
    const currentlyMultiLine = hasMultipleNodes || visuallyMultiLine;

    setIsMultiLine((prev) => {
      if (currentlyMultiLine) return true;
      if (prev && length === 0) return false;
      return prev;
    });
  }, [value, length]);

  const isUserCanSendMessage = useMemo(() => {
    return !isDisabled && length <= maxLength;
  }, [isDisabled, length, maxLength]);

  const handleSaveInputValue = (inputValue: string) => {
    const html = serializeSlateToHtml({
      type: 'paragraph',
      children: JSON.parse(inputValue),
    });
    const sanitizedHtml = DOMPurify.sanitize(html);
    setMessage(sanitizedHtml);

    return sanitizedHtml;
  };

  const clearInput = () => {
    const point = { path: [0, 0], offset: 0 };
    /* oxlint-disable react-hooks-js/immutability */
    // eslint-disable-next-line react-hooks/immutability
    editor.selection = { anchor: point, focus: point };
    /* oxlint-enable react-hooks-js/immutability */
    editor.children = getInitialValue('');
    /* oxlint-disable react-hooks-js/immutability */
    // eslint-disable-next-line react-hooks/immutability
    editor.history.undos = [];
    /* oxlint-enable react-hooks-js/immutability */
    editor.history.redos = [];
    setValue(getInitialValue(''));
    setIsMultiLine(false);
  };

  const handleMessageModeChange = useCallback((mode: MessageMode) => {
    setMessageMode(mode);
  }, []);

  const handleSchedule = useCallback(async () => {
    const fInput = handleSaveInputValue(JSON.stringify(value));
    if (!fInput) return;

    const success = await onSchedule(fInput);
    if (success) {
      clearInput();
      setIsFocused(false);
    }
  }, [value, onSchedule, clearInput]);

  const onKeyboardSend = useCallback(() => {
    if (!isUserCanSendMessage) return;

    if (messageMode === 'scheduled') {
      if (!isScheduleReady) return;

      handleSchedule();
      return;
    } else {
      const fInput = handleSaveInputValue(JSON.stringify(value));
      handleSendQuestion(fInput, { targetFiles: selectedFiles }); // TODO: Add Selected Folders
      clearInput();
    }

    setIsFocused(false);
  }, [
    value,
    handleSendQuestion,
    clearInput,
    setIsFocused,
    isUserCanSendMessage,
    messageMode,
    isScheduleReady,
    handleSchedule,
    selectedFiles,
  ]);

  const onChange = (val: Node[]) => {
    setValue(val);
  };

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        event.shiftKey ? editor.insertSoftBreak() : onKeyboardSend();
      }

      if (
        event.key.toLocaleLowerCase() === 'z' &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.shiftKey
          ? HistoryEditor.redo(editor)
          : HistoryEditor.undo(editor);
      }
      event.stopPropagation();
    },
    [editor, onKeyboardSend],
  );

  const focusEditor = (e: MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;

    if (
      target.closest('button') ||
      target.closest('[role="button"]') ||
      target.closest('[data-interactive]')
    ) {
      return;
    }

    if (!isReadOnlyAccess) {
      // Store current scroll position
      const scrollPosition = globalThis.scrollY;
      ReactEditor.focus(editor);
      // Restore scroll position
      globalThis.scrollTo({
        top: scrollPosition,
        behavior: 'instant',
      });
    }
  };

  const onFocus = () => {
    if (isDisabled) {
      return;
    }

    setIsFocused(true);
  };

  const handleFocus = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!isUserCanSendMessage) {
      e.preventDefault();
      return;
    }
    onFocus();
  };

  const onBlur = useCallback(() => {
    if (isDisabled) {
      return;
    }

    handleSaveInputValue(JSON.stringify(value));
    setIsFocused(false);
  }, [value, isDisabled]);

  // We also can use editor.insertData instead of onPaste, but it has open issue with pasting
  // html layout and from time to time it may crash
  const onPaste = useCallback(
    (event: ClipboardEvent<HTMLDivElement>, editor: CustomEditor) => {
      event.preventDefault();
      const { clipboardData } = event;
      const html = clipboardData?.getData('text/html');
      const sanitizedHtml = DOMPurify.sanitize(html ?? '');
      const parsed = new DOMParser().parseFromString(
        sanitizedHtml,
        'text/html',
      );
      const fragmentsRaw = htmlToSlateNodes(parsed.body);

      Transforms.insertFragment(editor, fragmentsRaw);
      return sanitizedHtml;
    },
    [],
  );

  const handleSend = () => {
    if (messageMode === 'scheduled') {
      handleSchedule();
    } else {
      handleSendQuestion(message, { targetFiles: selectedFiles }); // TODO: Add Selected Folders
      clearInput();
    }
  };

  const isFileSelectionVisible =
    showFileSelection && !isMultiLine && messageMode === 'chat';

  const AttachmentComponent = ff.aiFileSystem.enabled
    ? NodeSelection
    : FileSelection;

  return (
    <Slate editor={editor} value={preparedValue} onChange={onChange}>
      <div className={cn('relative', classNames)}>
        <div
          id="editorWrapper"
          ref={editorWrapperRef}
          className={cn(
            editorWrapperVariants({
              state: getEditorState(
                isUserCanSendMessage,
                isDisabled,
                isFocused,
                isReadOnlyAccess,
              ),
              hover: getHoverState(
                isUserCanSendMessage,
                isFocused,
                isReadOnlyAccess,
              ),
            }),
          )}
        >
          <div
            className={cn(
              'flex',
              showFileSelection && !isMultiLine
                ? 'flex-row items-center gap-1'
                : 'flex-col',
            )}
          >
            {isFileSelectionVisible && !isMultiLine && (
              <AttachmentComponent
                selectedFolders={selectedFolders}
                onSelectFolders={setSelectedFolders}
                isDisabled={!isUserCanSendMessage}
                selectedFiles={selectedFiles}
                onSelectFiles={setSelectedFiles}
              />
            )}
            <Editable
              className={cn(
                showFileSelection && !isMultiLine && 'min-w-0 flex-1',
              )}
              data-testId="editor"
              placeholder={placeholder ?? t('Ask me anything')}
              renderElement={renderElement}
              renderLeaf={renderEditorLeaf}
              onFocus={(e) => handleFocus(e)}
              onBlur={onBlur}
              onKeyDown={onKeyDown}
              onClick={focusEditor}
              onPaste={(event) => onPaste(event, editor)}
              readOnly={isDisabled || isReadOnlyAccess}
            />
          </div>
          <div
            className={cn(
              'sticky bottom-0 mt-4 flex flex-col gap-2 pb-2',
              isDisabled ? 'bg-action-disabled' : 'bg-ui-field02',
            )}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {messageMode === 'chat' && isMultiLine && (
                  <AttachmentComponent
                    selectedFolders={selectedFolders}
                    onSelectFolders={setSelectedFolders}
                    isDisabled={!isUserCanSendMessage}
                    selectedFiles={selectedFiles}
                    onSelectFiles={setSelectedFiles}
                  />
                )}
                {messageMode === 'scheduled' && (
                  <>
                    <ChipSelect
                      options={schedulingOptions}
                      placeholder={t('Select option')}
                      value={scheduling}
                      onValueChange={setScheduling}
                      renderValue={(_, opt) => opt?.label[0] ?? ''}
                    />
                    <ChipTimepicker
                      placeholder={t('Select time')}
                      minuteStep={5}
                      value={scheduleTime}
                      onChange={setScheduleTime}
                    />
                    <ChipDateRangePicker
                      placeholder={t('Date range')}
                      selected={scheduleDateRange}
                      onApply={setScheduleDateRange}
                      onReset={() =>
                        setScheduleDateRange({ from: undefined, to: undefined })
                      }
                    />
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                {!chatId && withScheduleChat && (
                  <MessageModeDropdown
                    value={messageMode}
                    onChange={handleMessageModeChange || !canScheduleChat}
                    isDisabled={isDisabled || !canScheduleChat}
                  />
                )}
                <SendMessageButton
                  hasMessage={!!length}
                  isInterruptSuccess={isInterruptSuccess}
                  isInterruptPending={isInterruptPending}
                  onInterrupt={onInterrupt}
                  isS2SMode={messageMode !== 'scheduled' && !length}
                  isDisabled={
                    !isUserCanSendMessage ||
                    (messageMode === 'scheduled' &&
                      (!isScheduleReady || isSchedulePending))
                  }
                  isInterruptible={isInterruptible}
                  handleSendQuestion={handleSend}
                  onStartVoiceChat={onStartVoiceChat}
                />
              </div>
            </div>
          </div>
        </div>
        {!!maxLength && (
          <div className="flex justify-end">
            <p
              className={typographyVariants({
                variant: 'bodyShort01',
                color: isDisabled ? 'disabled02' : 'secondary',
              })}
            >
              {length > MAX_CURRENT_TEXT_LENGTH
                ? `${MAX_CURRENT_TEXT_LENGTH}+`
                : length}
              /{maxLength}
            </p>
          </div>
        )}
      </div>
    </Slate>
  );
};

export { TextAreaAi };
